import type { Ref } from 'vue';
import { ref, computed, onMounted, onUnmounted, readonly, watch } from 'vue';
import { THEME } from '@pkg/config/misc/theme';
import { useIsDesktop } from '@pkg/ui/composables/useIsDesktop';
import type {
  DesktopNavStyle,
  HeaderBackgroundStyle,
  HeaderNavStyles,
  HeaderThemeState,
  MenuGridStyle,
  NavMenuGridInput,
} from '../types/headerTypes';

export type {
  DesktopNavStyle,
  HeaderBackgroundStyle,
  HeaderNavStyles,
  HeaderThemeState,
  NavMenuGridInput,
};

// --- Constants ---

const BLUR_CLOSED = 'blur(20px)';
const BLUR_OPEN = 'blur(24px)';

const BACKGROUND_CLOSED_LIGHT: HeaderBackgroundStyle = {
  backgroundColor: 'color-mix(in srgb, var(--theme-header-background-color-light) 0.7%, transparent)',
  backdropFilter: BLUR_CLOSED,
  webkitBackdropFilter: BLUR_CLOSED,
};
const BACKGROUND_CLOSED_DARK: HeaderBackgroundStyle = {
  backgroundColor: 'color-mix(in srgb, var(--theme-header-background-color-dark) 0.7%, transparent)',
  backdropFilter: BLUR_CLOSED,
  webkitBackdropFilter: BLUR_CLOSED,
};
const BACKGROUND_OPEN_LIGHT: HeaderBackgroundStyle = {
  backgroundColor: 'color-mix(in srgb, var(--theme-header-background-color-light) 80%, transparent)',
  backdropFilter: BLUR_OPEN,
  webkitBackdropFilter: BLUR_OPEN,
};
const BACKGROUND_OPEN_DARK: HeaderBackgroundStyle = {
  backgroundColor: 'color-mix(in srgb, var(--theme-header-background-color-dark) 80%, transparent)',
  backdropFilter: BLUR_OPEN,
  webkitBackdropFilter: BLUR_OPEN,
};

function getBaseThemeVars(themeState: HeaderThemeState): DesktopNavStyle {
  return {
    color: themeState.textColor,
    '--theme-header-background-color': themeState.isLightTheme
      ? 'var(--theme-header-background-color-dark)'
      : 'var(--theme-header-background-color-light)',
    '--theme-header-text-color': themeState.isLightTheme
      ? 'var(--theme-header-text-color-dark)'
      : 'var(--theme-header-text-color-light)',
  };
}

// --- Exported style getters ---

export function getDesktopNavStyle(themeState: HeaderThemeState, isMenuOpen: boolean): DesktopNavStyle {
  const bg = isMenuOpen ? themeState.backgroundCssOpenMenu : themeState.backgroundCss;
  return { ...getBaseThemeVars(themeState), ...bg };
}

/** Theme vars + no background; use for nav and bar when surface is open so only the surface paints the frosted layer. */
export function getTransparentStyleWhenOpen(themeState: HeaderThemeState): DesktopNavStyle {
  return {
    ...getBaseThemeVars(themeState),
    backgroundColor: 'transparent',
    backdropFilter: 'none',
    webkitBackdropFilter: 'none',
  };
}

/** Open-menu background only (for unified surface wrapper). Bar has no background when open. */
export function getSurfaceStyleOpenMenu(themeState: HeaderThemeState): DesktopNavStyle {
  return { ...themeState.backgroundCssOpenMenu };
}

export function getThemeBgClass(themeState: HeaderThemeState, prefix: string): string {
  return `${prefix}--bg-${themeState.isLightTheme ? 'light' : 'dark'}`;
}

export function getNavGridThemeClass(themeState: HeaderThemeState): 'nav__grid--bg-light' | 'nav__grid--bg-dark' {
  return getThemeBgClass(themeState, 'nav__grid') as 'nav__grid--bg-light' | 'nav__grid--bg-dark';
}

export function getNavGridBaseStyle(themeState: HeaderThemeState): DesktopNavStyle {
  return getBaseThemeVars(themeState);
}

export function getMenuGridStyle(themeState: HeaderThemeState): MenuGridStyle {
  return getNavGridBaseStyle(themeState);
}

export function getLogoTheme(themeState: HeaderThemeState): 'dark' | 'light' {
  return themeState.isLightTheme ? THEME.DARK : THEME.LIGHT;
}

/** Build HeaderNavStyles from theme state (for use without the composable, e.g. standalone/story). */
export function buildHeaderStyles(
  themeState: HeaderThemeState,
  isAnyDropdownOpen: boolean,
): HeaderNavStyles {
  return {
    desktopNavStyle: getDesktopNavStyle(themeState, isAnyDropdownOpen),
    transparentStyleWhenOpen: getTransparentStyleWhenOpen(themeState),
    surfaceStyleOpenMenu: getSurfaceStyleOpenMenu(themeState),
    navGridStyle: getNavGridBaseStyle(themeState),
    logoTheme: getLogoTheme(themeState),
    navGridThemeClass: getNavGridThemeClass(themeState),
  };
}

export const LIGHT_HEADER_THEME_STATE: HeaderThemeState = {
  currentTheme: THEME.LIGHT,
  isLightTheme: true,
  textColor: 'var(--theme-header-text-color-light)',
  backgroundCss: BACKGROUND_CLOSED_LIGHT,
  backgroundCssOpenMenu: BACKGROUND_OPEN_LIGHT,
};

/** Default (dark) theme when used outside header context. */
export const DEFAULT_HEADER_THEME_STATE: HeaderThemeState = {
  currentTheme: null,
  isLightTheme: false,
  textColor: 'var(--theme-header-text-color-dark)',
  backgroundCss: BACKGROUND_CLOSED_DARK,
  backgroundCssOpenMenu: BACKGROUND_OPEN_DARK,
};

// --- Composable ---

export interface UseHeaderThemeDetectionOptions {
  isAnyDropdownOpen?: Ref<boolean>;
}

export function useHeaderThemeDetection(options?: UseHeaderThemeDetectionOptions) {
  const isDesktop = useIsDesktop();
  const isAnyDropdownOpen = options?.isAnyDropdownOpen ?? ref(false);
  const themeState = ref<HeaderThemeState>({
    currentTheme: null,
    isLightTheme: false,
    textColor: 'var(--theme-header-text-color-dark)',
    backgroundCss: BACKGROUND_CLOSED_DARK,
    backgroundCssOpenMenu: BACKGROUND_OPEN_DARK,
  });

  const isLightTheme = (theme: THEME | null) =>
    theme === THEME.LIGHT || theme === THEME.LIGHT_ALT || theme === THEME.APP;

  const getTextColorForTheme = (theme: THEME | null) => {
    if (!theme || theme === THEME.APP) {
      return 'var(--theme-header-text-color-light)';
    }
    return isLightTheme(theme)
      ? 'var(--theme-header-text-color-light)'
      : 'var(--theme-header-text-color-dark)';
  };

  const getBackgroundStyleForTheme = (theme: THEME | null): HeaderBackgroundStyle => {
    if (!theme || theme === THEME.APP) {
      return BACKGROUND_CLOSED_LIGHT;
    }
    return isLightTheme(theme) ? BACKGROUND_CLOSED_LIGHT : BACKGROUND_CLOSED_DARK;
  };

  const getOpenMenuBackgroundStyleForTheme = (theme: THEME | null): HeaderBackgroundStyle => {
    if (!theme || theme === THEME.APP) {
      return BACKGROUND_OPEN_LIGHT;
    }
    return isLightTheme(theme) ? BACKGROUND_OPEN_LIGHT : BACKGROUND_OPEN_DARK;
  };

  const findSectionAtHeader = (sections: Element[]): Element | null => {
    const scrolled = sections.find((s) => {
      const { top, bottom } = s.getBoundingClientRect();
      return top <= 0 && bottom > 0;
    });
    if (scrolled) {
      return scrolled;
    }
    return sections.reduce<Element | null>((closest, section) => {
      const { top } = section.getBoundingClientRect();
      const closestTop = closest?.getBoundingClientRect().top ?? Infinity;
      return top >= 0 && top < closestTop ? section : closest;
    }, null);
  };

  const detectTheme = () => {
    if (typeof window === 'undefined') {
      return;
    }

    const sections = Array.from(document.querySelectorAll('[data-theme]'));
    const section = sections.length ? findSectionAtHeader(sections) : null;
    const theme = section?.getAttribute('data-theme') as THEME | null;

    themeState.value = {
      currentTheme: theme,
      isLightTheme: isLightTheme(theme),
      textColor: getTextColorForTheme(theme),
      backgroundCss: getBackgroundStyleForTheme(theme),
      backgroundCssOpenMenu: getOpenMenuBackgroundStyleForTheme(theme),
    };
  };

  let scrollHandler: (() => void) | null = null;
  let observer: MutationObserver | null = null;

  watch(isDesktop, detectTheme);

  onMounted(() => {
    // Run immediately so header text/background color is correct on first paint
    detectTheme();

    let ticking = false;
    scrollHandler = () => {
      if (ticking) {
        return;
      }
      ticking = true;
      requestAnimationFrame(() => {
        detectTheme();
        ticking = false;
      });
    };

    window.addEventListener('scroll', scrollHandler, { passive: true });

    observer = new MutationObserver(detectTheme);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-theme'],
    });

    setTimeout(detectTheme, 100);
  });

  onUnmounted(() => {
    if (scrollHandler) {
      window.removeEventListener('scroll', scrollHandler);
    }
    observer?.disconnect();
  });

  const headerStyles = computed<HeaderNavStyles>(() => {
    const theme = themeState.value;
    const anyOpen = isAnyDropdownOpen.value;
    return {
      desktopNavStyle: getDesktopNavStyle(theme, anyOpen),
      transparentStyleWhenOpen: getTransparentStyleWhenOpen(theme),
      surfaceStyleOpenMenu: getSurfaceStyleOpenMenu(theme),
      navGridStyle: getNavGridBaseStyle(theme),
      logoTheme: getLogoTheme(theme),
      navGridThemeClass: getNavGridThemeClass(theme),
    };
  });

  function getMenuGridStyleFor(_menu: NavMenuGridInput | undefined) {
    return getMenuGridStyle(themeState.value);
  }

  return {
    themeState: readonly(themeState),
    headerStyles,
    getMenuGridStyleFor,
  };
}
